<template>
  <div>

    <!-- Media -->
    <BMedia class="mb-2">
      <template #aside>
        <BAvatar ref="previewEl" :src="'/images/avatars/'+user.avatar" :text="avatarText(user.full_name)" :variant="`light-${resolveUserRoleVariant(user.role)}`" size="90px" rounded />
      </template>
      <h4 class="mb-1"> {{ user.name + " " + user.surname }} </h4>
      <div class="d-flex flex-wrap">
        <BButton variant="outline-danger" class="ml-1" @click="deletePopup(user.id, 'app-user/deleteUser', 'users')" v-if="ability.can('delete', 'user')">
          <span class="d-none d-sm-inline">{{$t('Delete')}}</span>
          <feather-icon icon="TrashIcon" class="d-inline d-sm-none" />
        </BButton>
      </div>
    </BMedia>

    <!-- User Info: Input Fields -->
    <ValidationObserver ref="form">
      <BForm @submit.prevent="onSubmit">
        <BRow>
          <TextInputValidated vsName="Username" :vsModel.sync="user.username" vsLabel="username" vsMd="4" vsRequired vsRules="required"  />
          <TextInputValidated vsName="Name" :vsModel.sync="user.name" vsLabel="name" vsMd="4" vsRequired vsRules="required" />
          <TextInputValidated vsName="Surname" :vsModel.sync="user.surname" vsLabel="surname" vsMd="4" vsRequired vsRules="required" />
          <TextInputValidated vsName="Email" :vsModel.sync="user.email" vsLabel="email" vsMd="4" vsRequired vsRules="required|email" />
          <VSelectValidated vsName="Status" :vsModel.sync="user.status" :vsOptions="statusOptions" vsLabel="label" vsMd="4" vsRequired vsRules="required" :vsReduce="status => status.value"  /> 
          <VSelectValidated vsName="User role" :vsModel.sync="user.roles[0]" :vsOptions="roles" vsLabel="name" vsMd="4" />
          <VSelectValidated vsName="Groups" :vsModel.sync="user.groups" :vsOptions="groups" vsLabel="name" vsMd="4" vsMultiple v-if="groups && groups.length>1" />
        </BRow>

      <div class="mt-4">
        <BFormCheckbox disabled class="rol-checkbox"> {{$t('Permissions inherited from the role')}} </BFormCheckbox>
        <BFormCheckbox disabled class="user-checkbox"> {{$t('User Permissions')}} </BFormCheckbox>
      </div>

      <!-- Permissions -->
      <BCard no-body class="border mt-1 mb-3" >
        <BCardHeader class="p-1">
          <BCardTitle class="font-medium-2">
            <feather-icon icon="LockIcon" size="18" />
            <span class="align-middle ml-50">{{$t('Permissions')}}</span>
          </BCardTitle>
        </BCardHeader>
        <BTable striped responsive class="mb-0" :items="permissions" :fields="fields" >
          <template #cell(module)="data"> {{ $t(data.value) }} </template>
          <template #cell(read)="data">   <BFormCheckbox :class="(data.item.read_from == 'Rol') ? 'rol-checkbox' : ''" :disabled="(data.item.read_from == 'Rol')" v-model="data.value" @change="check(data)" /> </template>
          <template #cell(write)="data">  <BFormCheckbox :class="(data.item.write_from == 'Rol') ? 'rol-checkbox' : ''" :disabled="(data.item.write_from == 'Rol')" v-model="data.value" @change="check(data)" /> </template>
          <template #cell(create)="data"> <BFormCheckbox :class="(data.item.create_from == 'Rol') ? 'rol-checkbox' : ''" :disabled="(data.item.create_from == 'Rol')" v-model="data.value" @change="check(data)" /> </template>
          <template #cell(delete)="data"> <BFormCheckbox :class="(data.item.delete_from == 'Rol') ? 'rol-checkbox' : ''" :disabled="(data.item.delete_from == 'Rol')" v-model="data.value" @change="check(data)" /> </template>
        </BTable>
      </BCard>

      <!-- Action Buttons -->
      <BButton variant="primary" class="mb-1 mb-sm-0 mr-0 mr-sm-1" :block="$store.getters['app/currentBreakPoint'] === 'xs'" @click="onSubmit" > {{ $t("Save Changes") }} </BButton>
      <BButton variant="outline-secondary" type="reset" :to="{ name:'users-view' }" :block="$store.getters['app/currentBreakPoint'] === 'xs'"> {{$t('Cancel')}} </BButton>
    </BForm>
  </ValidationObserver>

  </div>
</template>

<script>
import { BButton, BMedia, BAvatar, BRow, BCol, BFormGroup, BFormInput, BForm, BTable, BCard, BCardHeader, BCardTitle, BFormCheckbox, } from 'bootstrap-vue'
import { inject, ref } from '@vue/composition-api'
import { ValidationObserver } from 'vee-validate'
import { avatarText } from '@core/utils/filter'
import TextInputValidated from '@/components/forms/TextInputValidated.vue'
import VSelectValidated from '@/components/forms/vSelectValidated.vue'
import useUsersList from '../users-list/useUsersList'
import ability from '@/libs/acl/ability'
import popups from '@/hooks/popups'
import vSelect from 'vue-select'
import i18n from '@/libs/i18n'
import router from '@/router'
import store from '@/store'

export default {
  components: { BButton, BMedia, BAvatar, BRow, BCol, BFormGroup, BFormInput, BForm, BTable, BCard, BCardHeader, BCardTitle, BFormCheckbox, vSelect, TextInputValidated, VSelectValidated, ValidationObserver },
  setup() {
    const { resolveUserRoleVariant } = useUsersList()
    const { deletePopup, errorToast } = popups()
    const permissions = inject('permissions')
    const groups = inject('groups')
    const roles = inject('roles')
    const refInputEl = ref(null)
    const previewEl = ref(null)
    const user = inject('user')
    const form = ref(null)
    const fields = ref([
      { key: 'module', label: i18n.t('Module') },
      { key: 'read', label: i18n.t('Read') },
      { key: 'write', label: i18n.t('Write') },
      { key: 'create', label: i18n.t('Create') },
      { key: 'delete', label: i18n.t('Delete') },
    ])
    const statusOptions = [
      { label: i18n.t('Active'), value: true },
      { label: i18n.t('Inactive'), value: false },
    ]

    const check = function(data){
      if (data.field.key == "write")  { permissions.value[data.index].write  = data.value }
      if (data.field.key == "read")   { permissions.value[data.index].read   = data.value }
      if (data.field.key == "create") { permissions.value[data.index].create = data.value }
      if (data.field.key == "delete") { permissions.value[data.index].delete = data.value }
    }

    const onSubmit = function(){
        store.dispatch('users/updateUser', JSON.parse(JSON.stringify(user.value)) )
          .then(response => { router.push({ name: 'users' }) })
          .catch(error => { errorToast('Can\'t update user'); })
    }

    return {
      resolveUserRoleVariant,
      statusOptions,
      deletePopup,
      permissions,
      avatarText,
      refInputEl,
      previewEl,
      onSubmit,
      ability,
      groups,
      fields,
      check,
      roles,
      form,
      user
    }
  },
}
</script>

<style lang="scss">
  @import '~@core/scss/vue/libs/vue-select.scss';
  @import 'src/assets/scss/variables/_variables.scss';
  .user-checkbox label::before{ background-color: $primary !important; }
  .rol-checkbox label::before { background-color: lightcoral !important;  }
</style>
